import { FlaskConical } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { AISparkles } from "@/components/ui/ai-sparkles";
import { isNoChangesNeeded, type CosmoQuestion } from "@/types/scorecard";


const PRIORITY_RANK = { missing: 0, weak: 1, moderate: 2 } as const;

interface StrategicRecommendationsProps {
  questions: CosmoQuestion[];
  onCreateAbTest?: (question: CosmoQuestion) => void;
}

export function StrategicRecommendations({ questions, onCreateAbTest }: StrategicRecommendationsProps) {
  const gaps = questions
    .filter((q) => q.status !== "strong" && q.status !== "not_applicable")
    .filter((q) => !q.hasRunningTest)
    .filter((q) => !isNoChangesNeeded(q))
    .slice()
    .sort((a, b) => PRIORITY_RANK[a.status as keyof typeof PRIORITY_RANK] - PRIORITY_RANK[b.status as keyof typeof PRIORITY_RANK]);

  if (gaps.length === 0) return null;

  return (
    <Card variant="ai-plain">
      <CardContent className="p-6 space-y-4">
        <div className="flex items-start gap-2">
          <AISparkles className="h-4 w-4 mt-0.5 shrink-0" />
          <div>
            <h3 className="text-sm font-semibold text-foreground">Strategic Recommendations</h3>
            <p className="text-xs text-muted-foreground mt-0.5">One action per identified gap</p>
          </div>
        </div>
        <div className="divide-y divide-border">
          {gaps.map((g) => {
            const priority =
              g.status === "missing" || g.status === "weak"
                ? { label: "Medium", className: "bg-orange-100 text-foreground" }
                : { label: "Low", className: "bg-amber-100 text-foreground" };
            return (
              <div key={g.id} className="flex items-center gap-3 py-3 first:pt-0 last:pb-0">
                <div className="flex-1 flex items-center gap-2 min-w-0">
                  <p className="text-sm text-muted-foreground leading-relaxed">
                    {g.recommendation}
                  </p>
                  <Badge className={`shrink-0 ${priority.className} border-0 pointer-events-none`}>
                    {priority.label}
                  </Badge>
                </div>
                {onCreateAbTest && (
                  <Button
                    variant="ghost"
                    size="sm"
                    className="shrink-0 gap-1.5 text-primary"
                    onClick={() => onCreateAbTest(g)}
                  >
                    <FlaskConical className="h-3.5 w-3.5" />
                    A/B test
                  </Button>
                )}
              </div>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
}
